import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, Star, Calendar, Clock, PenSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { getDoctor, getReviews, getMe, createBooking, getBookingEnabled, trackEvent, createReview } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// Rating labels for display
const ratingLabels = {
  1: 'Very Poor',
  2: 'Poor',
  3: 'Okay',
  4: 'Good',
  5: 'Excellent'
};

// Star rating component for review form - moved outside main component
const StarRatingInput = ({ value, onChange, label }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-[#262626] mb-2">{label}</label>
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="p-1 hover:scale-110 transition-transform"
        >
          <Star
            className={`h-7 w-7 ${star <= value ? 'fill-[#2DD4BF] text-[#2DD4BF]' : 'text-slate-300 hover:text-[#2DD4BF]/50'}`}
          />
        </button>
      ))}
      {value > 0 && (
        <span className="ml-3 text-sm font-medium text-[#4B5563]">
          {ratingLabels[value]}
        </span>
      )}
    </div>
  </div>
);

export default function DoctorProfilePage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingEnabled, setBookingEnabled] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingData, setBookingData] = useState({
    patient_name: '',
    patient_phone: '',
    patient_email: '',
    preferred_date: '',
    preferred_time: '',
    reason: '',
    payment_method: 'cash'
  });
  
  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState({
    star_rating: 0,
    overall_experience: 0,
    office_bedside_manners: 0,
    clarity_explanation: 0,
    staff_friendliness: 0,
    text_review: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await getMe();
        setUser(response.data);
        setBookingData(prev => ({
          ...prev,
          patient_name: response.data.name,
          patient_email: response.data.email
        }));
      } catch (error) {
        setUser(null);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    loadData();
  }, [doctorId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [doctorRes, reviewsRes, bookingRes] = await Promise.all([
        getDoctor(doctorId),
        getReviews(doctorId),
        getBookingEnabled()
      ]);
      setDoctor(doctorRes.data);
      setReviews(reviewsRes.data);
      setBookingEnabled(bookingRes.data.enabled);
      
      // Track profile view
      await trackEvent('profile_view', doctorId);
    } catch (error) {
      console.error('Error loading doctor:', error);
      toast.error('Failed to load doctor profile');
    } finally {
      setLoading(false);
    }
  };

  const handleContactClick = async (method) => {
    await trackEvent('contact_click', doctorId, { method });
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please sign in to book an appointment');
      navigate('/login');
      return;
    }

    try {
      await createBooking({ ...bookingData, doctor_id: doctorId });
      toast.success('Booking request sent successfully!');
      setShowBookingModal(false);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to create booking');
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please sign in to write a review');
      navigate('/login');
      return;
    }

    // Validate all ratings are provided
    if (reviewData.star_rating === 0 || reviewData.overall_experience === 0 ||
        reviewData.office_bedside_manners === 0 || reviewData.clarity_explanation === 0 ||
        reviewData.staff_friendliness === 0) {
      toast.error('Please provide all ratings');
      return;
    }

    setSubmittingReview(true);
    try {
      await createReview({
        doctor_id: doctorId,
        ...reviewData
      });
      toast.success('Review submitted successfully!');
      setShowReviewModal(false);
      // Reset form
      setReviewData({
        star_rating: 0,
        overall_experience: 0,
        office_bedside_manners: 0,
        clarity_explanation: 0,
        staff_friendliness: 0,
        text_review: ''
      });
      // Reload reviews
      const reviewsRes = await getReviews(doctorId);
      setReviews(reviewsRes.data);
      // Reload doctor to get updated rating
      const doctorRes = await getDoctor(doctorId);
      setDoctor(doctorRes.data);
    } catch (error) {
      console.error('Review error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={() => setUser(null)} />
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2A44]"></div>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={() => setUser(null)} />
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="font-public text-[#4B5563]">Doctor not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Doctor Header */}
        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-6">
          <div className="flex items-start gap-8">
            {doctor.photo_url ? (
              <img
                src={doctor.photo_url}
                alt={doctor.name}
                className="w-32 h-32 rounded-xl object-cover"
              />
            ) : (
              <div className="w-32 h-32 rounded-xl bg-slate-200 flex items-center justify-center">
                <span className="text-5xl text-slate-500 font-manrope font-bold">
                  {doctor.name.charAt(0)}
                </span>
              </div>
            )}
            
            <div className="flex-1">
              <h1 className="font-manrope font-bold text-3xl text-[#0F2A44] mb-2">
                {doctor.title} {doctor.name}
              </h1>
              <p className="font-public text-lg text-[#4B5563] mb-4">{doctor.specialty}</p>
              
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 fill-[#2DD4BF] text-[#2DD4BF]" />
                  <span className="font-space font-medium text-lg text-[#262626]">
                    {doctor.average_rating.toFixed(1)}
                  </span>
                  <span className="text-[#4B5563] font-public">
                    ({doctor.total_reviews} reviews)
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-[#4B5563]" />
                  <span className="text-[#4B5563] font-public">{doctor.region}</span>
                </div>
              </div>
              
              {/* Contact Actions */}
              <div className="flex flex-wrap gap-3">
                {doctor.contact_phone && (
                  <Button
                    data-testid="contact-phone-btn"
                    onClick={() => {
                      handleContactClick('phone');
                      window.location.href = `tel:${doctor.contact_phone}`;
                    }}
                    className="bg-[#4A90E2] hover:bg-[#4A90E2]/90 text-white rounded-lg flex-1 sm:flex-none"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Call
                  </Button>
                )}
                
                {doctor.contact_whatsapp && (
                  <Button
                    data-testid="contact-whatsapp-btn"
                    onClick={() => {
                      handleContactClick('whatsapp');
                      window.open(`https://wa.me/${doctor.contact_whatsapp.replace(/\D/g, '')}`, '_blank');
                    }}
                    className="bg-[#25D366] hover:bg-[#25D366]/90 text-white rounded-lg flex-1 sm:flex-none"
                  >
                    WhatsApp
                  </Button>
                )}
                
                {bookingEnabled && (
                  <Button
                    data-testid="book-appointment-btn"
                    onClick={() => setShowBookingModal(true)}
                    className="bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] rounded-lg w-full sm:w-auto"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">Book Appointment</span>
                    <span className="inline sm:hidden">Book</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Bio */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="font-manrope font-semibold text-xl text-[#0F2A44] mb-4">About</h2>
            <p className="font-public text-[#4B5563] leading-relaxed mb-4">{doctor.bio || 'No bio available.'}</p>
            
            {doctor.education && (
              <div className="mb-4">
                <h3 className="font-manrope font-semibold text-[#0F2A44] mb-2">Education</h3>
                <p className="font-public text-[#4B5563]">{doctor.education}</p>
              </div>
            )}
            
            {doctor.credentials && (
              <div>
                <h3 className="font-manrope font-semibold text-[#0F2A44] mb-2">Credentials</h3>
                <p className="font-public text-[#4B5563]">{doctor.credentials}</p>
              </div>
            )}
          </div>
          
          {/* Contact Info */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="font-manrope font-semibold text-xl text-[#0F2A44] mb-4">Contact Information</h2>
            
            <div className="space-y-3">
              {doctor.contact_phone && (
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-[#4A90E2] mt-0.5" />
                  <span className="font-public text-sm text-[#4B5563]">{doctor.contact_phone}</span>
                </div>
              )}
              
              {doctor.contact_email && (
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#4A90E2] mt-0.5" />
                  <span className="font-public text-sm text-[#4B5563] break-all">{doctor.contact_email}</span>
                </div>
              )}
              
              {doctor.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#4A90E2] mt-0.5" />
                  <span className="font-public text-sm text-[#4B5563]">{doctor.address}</span>
                </div>
              )}
              
              {doctor.website && (
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-[#4A90E2] mt-0.5" />
                  <a href={doctor.website} target="_blank" rel="noopener noreferrer" className="font-public text-sm text-[#4A90E2] hover:underline break-all">
                    {doctor.website}
                  </a>
                </div>
              )}
            </div>
            
            {doctor.insurances_accepted && doctor.insurances_accepted.length > 0 && (
              <div className="mt-6">
                <h3 className="font-manrope font-semibold text-[#0F2A44] mb-3">Insurances Accepted</h3>
                <div className="flex flex-wrap gap-2">
                  {doctor.insurances_accepted.map((insurance, idx) => (
                    <span key={idx} className="px-3 py-1 bg-slate-100 text-[#4B5563] text-xs rounded-full font-public">
                      {insurance}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {doctor.opening_hours && Object.keys(doctor.opening_hours).length > 0 && (
              <div className="mt-6">
                <h3 className="font-manrope font-semibold text-[#0F2A44] mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[#4A90E2]" />
                  Opening Hours
                </h3>
                <div className="space-y-2">
                  {[
                    { key: 'weekdays', label: 'Mon - Fri' },
                    { key: 'saturday', label: 'Saturday' },
                    { key: 'sunday', label: 'Sunday' },
                    { key: 'public_holidays', label: 'Public Holidays' }
                  ].map(({ key, label }) => {
                    const val = doctor.opening_hours[key];
                    if (!val) return null;
                    const isClosed = val === 'Closed';
                    return (
                      <div key={key} className="flex justify-between items-center text-sm" data-testid={`profile-hours-${key}`}>
                        <span className="text-[#4B5563] font-public">{label}</span>
                        <span className={`font-space font-medium ${isClosed ? 'text-red-400' : 'text-[#0F2A44]'}`}>
                          {val}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reviews */}
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-manrope font-semibold text-xl text-[#0F2A44]">
              Patient Reviews ({reviews.length})
            </h2>
            <Button
              onClick={() => {
                if (!user) {
                  toast.error('Please sign in to write a review');
                  navigate('/login');
                  return;
                }
                setShowReviewModal(true);
              }}
              data-testid="write-review-btn"
              className="bg-[#4A90E2] hover:bg-[#4A90E2]/90 text-white rounded-lg"
            >
              <PenSquare className="h-4 w-4 mr-2" />
              Write Review
            </Button>
          </div>
          
          {reviews.length === 0 ? (
            <p className="font-public text-[#4B5563] text-center py-8">No reviews yet. Be the first to review this doctor!</p>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.review_id} className="border-b border-slate-100 last:border-0 pb-6 last:pb-0 mb-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-manrope font-semibold text-[#0F2A44]">{review.user_name}</p>
                        <span className="font-public text-xs text-[#4B5563] ml-4">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${i < review.star_rating ? 'fill-[#2DD4BF] text-[#2DD4BF]' : 'text-slate-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {review.text_review && (
                    <p className="font-public text-sm text-[#4B5563] mt-2">{review.text_review}</p>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                    <div className="text-xs">
                      <span className="text-[#4B5563] font-public">Overall: </span>
                      <span className="font-space font-medium text-[#2DD4BF]">{review.overall_experience}/5</span>
                    </div>
                    <div className="text-xs">
                      <span className="text-[#4B5563] font-public">Manners: </span>
                      <span className="font-space font-medium text-[#2DD4BF]">{review.office_bedside_manners}/5</span>
                    </div>
                    <div className="text-xs">
                      <span className="text-[#4B5563] font-public">Clarity: </span>
                      <span className="font-space font-medium text-[#2DD4BF]">{review.clarity_explanation}/5</span>
                    </div>
                    <div className="text-xs">
                      <span className="text-[#4B5563] font-public">Staff: </span>
                      <span className="font-space font-medium text-[#2DD4BF]">{review.staff_friendliness}/5</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope text-2xl text-[#0F2A44]">Book Appointment</DialogTitle>
            <DialogDescription className="font-public text-[#4B5563]">
              Fill in your details to book an appointment with {doctor.title} {doctor.name}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleBooking} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#262626] mb-1">Name</label>
              <Input
                required
                value={bookingData.patient_name}
                onChange={(e) => setBookingData({ ...bookingData, patient_name: e.target.value })}
                data-testid="booking-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#262626] mb-1">Phone</label>
              <Input
                required
                type="tel"
                value={bookingData.patient_phone}
                onChange={(e) => setBookingData({ ...bookingData, patient_phone: e.target.value })}
                data-testid="booking-phone-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#262626] mb-1">Email</label>
              <Input
                required
                type="email"
                value={bookingData.patient_email}
                onChange={(e) => setBookingData({ ...bookingData, patient_email: e.target.value })}
                data-testid="booking-email-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-1">Preferred Date</label>
                <Input
                  required
                  type="date"
                  value={bookingData.preferred_date}
                  onChange={(e) => setBookingData({ ...bookingData, preferred_date: e.target.value })}
                  data-testid="booking-date-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-1">Preferred Time</label>
                <Input
                  required
                  type="time"
                  value={bookingData.preferred_time}
                  onChange={(e) => setBookingData({ ...bookingData, preferred_time: e.target.value })}
                  data-testid="booking-time-input"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#262626] mb-1">Reason for Visit (Optional)</label>
              <Textarea
                value={bookingData.reason}
                onChange={(e) => setBookingData({ ...bookingData, reason: e.target.value })}
                data-testid="booking-reason-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#262626] mb-1">Payment Method</label>
              <select
                value={bookingData.payment_method}
                onChange={(e) => setBookingData({ ...bookingData, payment_method: e.target.value })}
                data-testid="booking-payment-select"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white font-public"
              >
                <option value="cash">Cash</option>
                <option value="insurance">Insurance</option>
              </select>
            </div>
            
            <Button
              type="submit"
              data-testid="booking-submit-btn"
              className="w-full bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] font-manrope font-semibold"
            >
              Confirm Booking
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope text-2xl text-[#0F2A44]">Write a Review</DialogTitle>
            <DialogDescription className="font-public text-[#4B5563]">
              Share your experience with {doctor.title} {doctor.name}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleReviewSubmit} className="space-y-4">
            {/* Overall Star Rating */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <StarRatingInput
                label="Overall Rating *"
                value={reviewData.star_rating}
                onChange={(val) => setReviewData({ ...reviewData, star_rating: val })}
              />
            </div>

            {/* Standardized Ratings */}
            <div className="space-y-4">
              <p className="text-sm font-medium text-[#262626]">Rate the following aspects:</p>
              
              <StarRatingInput
                label="Overall Experience *"
                value={reviewData.overall_experience}
                onChange={(val) => setReviewData({ ...reviewData, overall_experience: val })}
              />
              
              <StarRatingInput
                label="Doctor's Office & Bedside Manners *"
                value={reviewData.office_bedside_manners}
                onChange={(val) => setReviewData({ ...reviewData, office_bedside_manners: val })}
              />
              
              <StarRatingInput
                label="Clarity of Explanation *"
                value={reviewData.clarity_explanation}
                onChange={(val) => setReviewData({ ...reviewData, clarity_explanation: val })}
              />
              
              <StarRatingInput
                label="Friendliness of Staff *"
                value={reviewData.staff_friendliness}
                onChange={(val) => setReviewData({ ...reviewData, staff_friendliness: val })}
              />
            </div>

            {/* Text Review */}
            <div>
              <label className="block text-sm font-medium text-[#262626] mb-2">
                Your Review (Optional)
              </label>
              <Textarea
                placeholder="Share more details about your experience..."
                value={reviewData.text_review}
                onChange={(e) => setReviewData({ ...reviewData, text_review: e.target.value })}
                className="min-h-[100px]"
                data-testid="review-text-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submittingReview}
              data-testid="review-submit-btn"
              className="w-full bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] font-manrope font-semibold"
            >
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}